import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {ValidationHelper} from '@core/helpers';
import {
  BIO_MAX_LENGTH,
  EMAIL_MAX_LENGTH,
  EMAIL_REGEXP,
  ERROR_MSG,
  FACEBOOK_REGEXP,
  INSTAGRAM_REGEXP,
  LINKEDIN_REGEXP,
  PHONE_NUMBER_MASK,
  TWITCH_REGEXP,
  TWITTER_REGEXP,
  URL_REGEXP,
  USERNAME_MAX_LENGTH,
  VIMEO_REGEXP,
  YOUTUBE_REGEXP,
} from '@core/constants';
import {AvailableDays, PlaceChange, StateInterface, UserProfileForm,} from '@core/models';
import {map, startWith, takeUntil} from 'rxjs/operators';
import {Observable, Subject} from 'rxjs';
import { AsyncPipe, JsonPipe } from '@angular/common';
import {WeekDaysComponent} from '@shared/components';
import {NgxMaskDirective} from 'ngx-mask';
import {CustomValidator} from '@core/validators';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';

@Component({
    selector: 'app-profile-form',
    templateUrl: './profile-form.component.html',
    styleUrls: ['./profile-form.component.scss'],
    imports: [
    ReactiveFormsModule,
    MatSlideToggleModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    AsyncPipe,
    WeekDaysComponent,
    NgxMaskDirective,
]
})
export class ProfileFormComponent implements OnInit, OnDestroy {
  @Input() set userData(data: UserProfileForm) {
    if (!data) {
      return;
    }
    this.weekDays = data.available_days;
    this.profileForm.patchValue({...data});
    if (Object.keys(data.social_medias).length) {
      this.profileForm
        .get('social_medias')
        .patchValue({...data.social_medias});
    }
  }

  @Input() countries: StateInterface[] = [];

  @Input() set states(states: StateInterface[]) {
    this.allStates = states || [];
  }

  @Input() set cities(cities: { id: number; name: string }[]) {
    this.allCities = cities || [];
  }

  @Input() loading = false;
  @Input() showChangePassword = true;
  @Input() showPrivateToggle = true;
  @Output() placeChanged: EventEmitter<PlaceChange> = new EventEmitter<PlaceChange>();
  protected readonly ERROR_MSG = ERROR_MSG;
  protected readonly BIO_MAX_LENGTH = BIO_MAX_LENGTH;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private allStates: StateInterface[] = [];
  private allCities: { id: number; name: string }[] = [];
  private requiredFields: string[] = [
    'username',
    'fullname',
    'phone_number',
    'email',
  ];

  public filteredStates: Observable<StateInterface[]>;
  public filteredCities: Observable<{ id: number; name: string }[]>;
  public weekDays: AvailableDays = {} as AvailableDays;
  public isDisabled = true;
  public profileForm: FormGroup = this._createFormGroup();
  public maxInputLength = USERNAME_MAX_LENGTH + 1;
  public emailMaxLength = EMAIL_MAX_LENGTH;
  public phoneNumberMask = PHONE_NUMBER_MASK;

  constructor(
    private _fb: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.listenCountryStateChange();
    this.listenCityChange();
  }

  private _createFormGroup(): FormGroup {
    return this._fb.group({
      username: [
        '',
        [
          CustomValidator.required,
          ValidationHelper.minLengthValidator,
          Validators.maxLength(USERNAME_MAX_LENGTH),
        ],
      ],
      fullname: [
        '',
        [
          CustomValidator.required,
          ValidationHelper.minLengthValidator,
          Validators.maxLength(USERNAME_MAX_LENGTH),
        ],
      ],
      phone_number: [
        '',
        [
          // ValidationHelper.phoneNumberValidator()
        ],
      ],
      email: [
        '',
        [
          CustomValidator.required,
          Validators.pattern(EMAIL_REGEXP),
        ],
      ],
      bio: ['', [Validators.maxLength(BIO_MAX_LENGTH)]],
      country: '',
      state: '',
      city: '',
      social_medias: this._fb.group({
        youtube: ['', [Validators.pattern(YOUTUBE_REGEXP)]],
        twitch: ['', [Validators.pattern(TWITCH_REGEXP)]],
        twitter: ['', [Validators.pattern(TWITTER_REGEXP)]],
        facebook: ['', [Validators.pattern(FACEBOOK_REGEXP)]],
        linkedin: ['', [Validators.pattern(LINKEDIN_REGEXP)]],
        vimeo: ['', [Validators.pattern(VIMEO_REGEXP)]],
        instagram: ['', [Validators.pattern(INSTAGRAM_REGEXP)]],
      }),
      visibility_id: [''],
      available_days: '',
    });
  }

  private listenCountryStateChange(): void {
    this.filteredStates = this.profileForm.get('state').valueChanges.pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return name
          ? this.filter<StateInterface>(name as string, this.allStates)
          : [];
      })
    );
  }

  private listenCityChange(): void {
    this.filteredCities = this.profileForm.get('city').valueChanges.pipe(
      startWith(''),
      takeUntil(this.destroy$),
      map((value) => {
        const name = typeof value === 'string' ? value : value?.name;
        return name
          ? this.filter<{ name: string; id: number }>(
            name as string,
            this.allCities
          )
          : [];
      })
    );
  }

  private filter<T extends { name: string }>(
    name: string,
    filterList: T[]
  ): T[] {
    const filterValue = name.toLowerCase();
    return filterList.filter((option) =>
      option.name.toLowerCase().includes(filterValue)
    );
  }

  public getFormValue(fieldName: string): string {
    return this.profileForm['controls'].social_medias.get(fieldName)?.value;
  }


  public countryChanged(event): void {
    this.profileForm.get('state').reset();
    this.profileForm.get('city').reset();
    this.placeChanged.emit({country: event});
  }

  public stateSelected(value: MatAutocompleteSelectedEvent): void {
    this.placeChanged.emit({
      country: this.profileForm.get('country').value,
      state: value.option.value.iso2,
    });
    this.profileForm.get('state').setValue(value.option.value.name);
  }

  public redirectToSocialMedia(url: string): void {
    if (url && this.isDisabled && url.match(URL_REGEXP)) {
      window.open(url, '_blank');
    }
  }

  get mediaControls(): FormGroup {
    return this.profileForm['controls'].social_medias as FormGroup;
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
}
